import { useCallback, useState } from 'react';

/**
 * Drives the void/unvoid actions on a list of attachments: holds the row being
 * voided, the reason the user is typing, and the in-flight state. Voiding opens
 * a modal because a reason is required; unvoiding is a single click.
 *
 * Pair with `AttachmentVoidModal`, which renders from the returned control.
 */
export interface UseAttachmentVoidOptions<T> {
  /** What the modal calls the file, e.g. 'photo' or 'document'. Defaults to 'attachment'. */
  noun?: string;
  /** The file's name, shown in the modal so the user can see what they picked. */
  label?: (row: T) => string;
  onVoid: (row: T, reason: string) => Promise<void>;
  onUnvoid: (row: T) => Promise<void>;
  /** Where to surface an unvoid failure, which has no modal to show it in. */
  onError?: (message: string) => void;
}

export interface AttachmentVoidControl<T> {
  noun: string;
  target: T | null;
  targetLabel: string;
  reason: string;
  setReason: (value: string) => void;
  error: string;
  busy: boolean;
  /** Opens the reason modal for this attachment. */
  requestVoid: (row: T) => void;
  cancel: () => void;
  confirm: () => void;
  /** Restores a voided attachment straight away. */
  unvoid: (row: T) => void;
}

export function useAttachmentVoid<T>(options: UseAttachmentVoidOptions<T>): AttachmentVoidControl<T> {
  const { noun = 'attachment', label, onVoid, onUnvoid, onError } = options;
  const [target, setTarget] = useState<T | null>(null);
  const [reason, setReasonState] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const requestVoid = useCallback((row: T) => {
    setTarget(row);
    setReasonState('');
    setError('');
  }, []);

  const cancel = useCallback(() => {
    setTarget(null);
    setReasonState('');
    setError('');
  }, []);

  const setReason = useCallback((value: string) => {
    setReasonState(value);
    setError('');
  }, []);

  const confirm = useCallback(() => {
    if (target == null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(`A reason is required to void this ${noun}.`);
      return;
    }
    setBusy(true);
    void (async () => {
      try {
        await onVoid(target, trimmed);
        setTarget(null);
        setReasonState('');
      } catch (e) {
        setError(e instanceof Error ? e.message : `Failed to void the ${noun}.`);
      } finally {
        setBusy(false);
      }
    })();
  }, [noun, onVoid, reason, target]);

  const unvoid = useCallback(
    (row: T) => {
      setBusy(true);
      void (async () => {
        try {
          await onUnvoid(row);
        } catch (e) {
          const message = e instanceof Error ? e.message : `Failed to restore the ${noun}.`;
          if (onError) onError(message);
          else setError(message);
        } finally {
          setBusy(false);
        }
      })();
    },
    [noun, onError, onUnvoid]
  );

  return {
    noun,
    target,
    targetLabel: target != null && label ? label(target) : '',
    reason,
    setReason,
    error,
    busy,
    requestVoid,
    cancel,
    confirm,
    unvoid,
  };
}
